import AutosaveSuccess from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Accordion, AccordionDetails, AccordionSummary, CircularProgress, Divider } from "@mui/material";
import { get, includes } from "lodash";
import queryString from "query-string";
import { useEffect, useState } from "react";
import ReactGA from "react-ga4";
import { connect } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import NotificationModal from "../auth/NotificationModal";
import Comments from "../comments/CommentsContainer";
import comments_constants from "../comments/constants";
import comments_operations from "../comments/ducks/operations";
import contactConstants from "../contact/constants";
import contactOperations from "../contact/ducks/operations";
import DocumentDrop from "../documents/DocumentDropContainer";
import document_operations from "../documents/ducks/operations";
import Property from "../property/Property";
import PropertyUpdateModalContainer from "../property/PropertyUpdateModalContainer";
import property_operations from "../property/ducks/operations";
import searchOperations from "../search/ducks/operations";
import utils from "../utils/index";
import ListingRequest from "./ListingRequest";
import ListingToolbar from "./ListingToolbar";
import constants from "./constants";
import operations from "./ducks/operations";

const ListingRequestContainer = (props) => {
    const { userProfile, handleSubmit, toggleComments } = props;
    const [showRequestUpdate, setShowRequestUpdate] = useState(false);
    const [showSubmit, setShowSubmit] = useState(false);
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    useEffect(() => {
        const queryParams = queryString.parse(location.search);
        const action = get(queryParams, "action", null);
        const comments = queryParams?.comments ?? false;
        if (id) {
            if (action === "start" && (userProfile.role === "dataAdmin" || userProfile.role === "dataEntry")) {
                handleSubmit(false, constants.urls.assign_listing_for_review(id), true, id);
            } else {
                initListingAndRedirect(action);
            }
            if (comments) {
                toggleComments();
            }
        }

        ReactGA.send({ hitType: "pageview", page: "/listing-beta/*", title: `Listing Request: ${id}` });

        return () => {
            props.clearListingState();
            props.clearUtilsErrors();
        };
    }, []);

    const initListingAndRedirect = async (action) => {
        const isDuplicate = await props.initListing(id, action);
        isDuplicate && navigate(`/listing/${id}`);
    };

    const shouldDisableListingInputs = () => {
        let disableListingInput = props.disableInputs;
        if (get(props.listing, "nonKmListingFlg", false) === true) {
            // is outside listing
            if (
                get(props.listing, "status") === "Update Requested" &&
                includes(["dataEntry", "dataAdmin"], get(props.userProfile, "role"))
            ) {
                disableListingInput = false;
            } else if (get(props.listing, "status") === "Active") {
                // Requirement 6.10.19 anyone can edit active outside listings
                disableListingInput = false;
            } else {
                disableListingInput = get(props.listing, "auditCreatedBy", null) !== get(props.userProfile, "user");
            }
        } else if (props.listing.status === "Draft" || props.listing.status === "Broker Action Required") {
            disableListingInput = get(props.listing, "auditCreatedBy", null) !== get(props.userProfile, "user");
        }
        return disableListingInput;
    };

    const disableListingInput = shouldDisableListingInputs();

    const setRequestUpdateAndSubmit = (listing, userProfile) => {
        const activeStatus = ["Update Requested", "Active Broker Action Required", "Active", "Pending"].includes(
            listing.status
        );
        const dataAdminOrDataEntry = ["dataAdmin", "dataEntry"].includes(userProfile.role);
        const kmListing = !listing.nonKmListingFlg;

        if (activeStatus && dataAdminOrDataEntry && kmListing) {
            setShowRequestUpdate(false);
            setShowSubmit(true);
        } else if (activeStatus && kmListing) {
            setShowRequestUpdate(true);
            setShowSubmit(false);
        } else if (activeStatus && !kmListing) {
            setShowRequestUpdate(true);
            setShowSubmit(true);
        } else {
            setShowRequestUpdate(false);
            setShowSubmit(true);
        }
    };

    const handleTriggerPropertySearchModal = (e) => {
        e.stopPropagation();
        props.togglePropertySearchModal(true);
    };
    const showFeatureModal = () => {
        return props.listing.status === "Draft" || props.listing.status === "Broker Action Required";
    };
    return (
        <div>
            {props.listing.status && <NotificationModal displayModal={showFeatureModal()} />}

            <div style={{ minWidth: "800px" }}>
                <ListingToolbar {...props} disableInputs={disableListingInput} />
                <div>
                    <div>
                        <div>
                            {props.listing.listingKey ? (
                                <DocumentDrop
                                    entityKey={id}
                                    entityStatus={props.listing?.status}
                                    type={"LISTING"}
                                    documentTypes={
                                        get(props.listing, "nonKmListingFlg") ? "listingFormOutside" : "listingForm"
                                    }
                                    formDisabled={props.disableListingInput}
                                    errors={get(props.errors, "listing.documents")}
                                    parentType={get(props.listing, "nonKmListingFlg") ? "OUTSIDE LISTING" : "LISTING"}
                                    stateCode={
                                        get(props.listing, "attributes.address.city.state.stateCode", null)
                                            ? get(props.listing, "attributes.address.city.state.stateCode")
                                            : get(props.property, "primaryAddress.city.state.stateCode", null)
                                    }
                                />
                            ) : (
                                <div className="circularProgressCentered">
                                    <CircularProgress />
                                </div>
                            )}
                        </div>
                        <Accordion className="expansionPanelBorder">
                            {(get(props.userProfile, "role") === "dataAdmin" &&
                                get(props.listing, "status") === "Active") ||
                            (includes(["dataEntry", "dataAdmin"], get(props.userProfile, "role")) &&
                                includes(["Active", "Inactive"], get(props.listing, "status"))) ||
                            disableListingInput === false ? (
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <div className="expansionPanelContentContainer">
                                        <AutosaveSuccess className="waterloo" style={{ paddingTop: "4px" }} />
                                        <div>
                                            Property Information
                                            <EditIcon
                                                style={{
                                                    width: "18px",
                                                    height: "18px",
                                                    fill: "#233a47",
                                                    paddingLeft: "5px",
                                                }}
                                                onClick={(e) => handleTriggerPropertySearchModal(e)}
                                            />
                                            <div>
                                                <small className="entityIDText">
                                                    Property ID: {props.property.propertyKey}
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                </AccordionSummary>
                            ) : (
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <div className="expansionPanelContentContainer">
                                        <AutosaveSuccess className="waterloo" style={{ paddingTop: "4px" }} />
                                        <div>
                                            Property Information
                                            <div>
                                                <small className="entityIDText">
                                                    Property ID: {props.property.propertyKey}
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                </AccordionSummary>
                            )}
                            <Divider />
                            <AccordionDetails>
                                {props.isLoading ? ( // Spinner
                                    <div className="circularProgressCentered">
                                        <CircularProgress color="primary" />
                                    </div>
                                ) : (
                                    <Property
                                        {...props}
                                        attributes={props.propertyAttributes}
                                        handleInputChange={props.handlePropertyInputChange}
                                        handleAddArrayElement={props.handlePropertyAddArrayElement}
                                        handleUpdateArrayElement={props.handlePropertyUpdateArrayElement}
                                        handleDeleteArrayElement={props.handlePropertyDeleteArrayElement}
                                        disableInputs={true}
                                        userProfile={props.userProfile}
                                    />
                                )}
                            </AccordionDetails>
                        </Accordion>
                        <ListingRequest
                            {...props}
                            autocompleteResults={props.autocompleteResults}
                            handleSearchCompany={props.handleSearchCompany}
                            handleDeleteArrayElement={props.handleListingDeleteArrayElement}
                            handleAddArrayElement={props.handleAddArrayElement}
                            handleDeleteArrayElementNEW={props.handleDeleteArrayElementNEW}
                            handleUpdateArrayElement={props.handleListingUpdateArrayElement}
                            handleListingUpdateArrayElement={props.handleUpdateArrayElement}
                            data={props.listing}
                            property={props.property}
                            disableInputs={disableListingInput}
                        />
                    </div>

                    <div style={{ minWidth: "50%" }}>
                        <Comments
                            {...props}
                            showRequestUpdateListing={showRequestUpdate}
                            showSubmitListing={showSubmit}
                            setRequestUpdateAndSubmit={setRequestUpdateAndSubmit}
                            parentRecordType={comments_constants.LISTNG_REQUEST_PARENT_RECORD_TYPE}
                            parentRecordId={id}
                        />
                    </div>
                </div>
            </div>
            <div>
                <PropertyUpdateModalContainer {...props} formType="listing" />
            </div>
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        userProfile: state.authReducer.userProfile,
        errors: state.utilsReducer.errors,
        expandedErrorPanels: state.utilsReducer.panelsToExpand,
        checkErrorPanels: state.utilsReducer.checkErrorPanels,
        snackbar: state.utilsReducer.snackbar,
        numComments: state.commentsReducer.comments.length,
        confirmDialog: state.utilsReducer.confirmDialog,
        inactivateListingDialog: state.listingReducer.inactivateListingDialog,
        deleteListingDialog: state.listingReducer.deleteListingDialog,
        duplicateListingDialog: state.listingReducer.listingDuplicateDialog,
        duplicateSuccessDialog: state.listingReducer.duplicateSuccessDialog,
        panelsExpanded: get(state.listingReducer, "panelsExpanded", {}),
        requestModal: get(state.listingReducer, "requestModal", {}),
        property: get(state.listingReducer, "listing.property", {}),
        propertyAttributes: get(state.listingReducer, "listing.property.propertyAttributes", {}),
        listing: get(state.listingReducer, "listing", {}),
        listingAttributes: get(state.listingReducer, "listing.attributes", {}),
        listingKey: get(state.listingReducer, "listing.listingKey", {}),
        listingType: get(state.listingReducer, "listing.listingType", {}),
        listingContacts: get(state.listingReducer, "listing.attributes.contacts", []),
        searchResults: state.dealReducer.searchResults ? state.dealReducer.searchResults : {},
        companyInformation: state.dealReducer.companyInformation ? state.dealReducer.companyInformation : {},
        isLoading: state.utilsReducer.isLoading,
        autocompleteResults: state.searchReducer.autocompleteResults,
        listingBrokers: get(state.listingReducer, "listing.listingBrokers", [{}]),
        disableInputs: get(
            constants.disabledStatuses,
            `${state.authReducer.userProfile.role}.${state.listingReducer.listing.status}`,
            get(constants.disabledStatuses.default, state.listingReducer.listing.status)
        ),
        elementsSaving: state.utilsReducer.elementsSaving,
        documentTypes: state.documentsReducer.savedDocuments,
        propDocumentTypes: state.documentsReducer.savedPropDocuments,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        getListing: (listingId) => {
            dispatch(operations.getListing(listingId));
        },
        initListing: (listingId, action) => {
            return dispatch(operations.initListing(listingId, action));
        },
        handleInputChange: (field, value) => {
            dispatch(operations.handleListingInputChange(field, value));
        },
        handlePropertyInputChange: (field, value) => {
            dispatch(operations.handleListingInputChange("listing." + field, value));
        },
        toggleSnackBar: (open, variant, message) => {
            dispatch(utils.operations.snackbar(open, variant, message));
        },
        handleSave: () => {
            dispatch(operations.saveListing());
        },
        handleSaveAndExit: () => {
            return dispatch(operations.saveAndExit());
        },
        toggleComments: () => {
            dispatch(comments_operations.toggleComments());
        },
        handleSubmit: (save, callback, confirmIsOpen, key) => {
            if (!confirmIsOpen) {
                dispatch(utils.operations.toggleConfirm());
            }
            return dispatch(operations.handleSubmit(save, callback, confirmIsOpen, key));
        },
        toggleConfirm: (onSubmit) => {
            dispatch(utils.operations.toggleConfirm(onSubmit));
        },
        handlePropertyAddArrayElement: (arrayPath) => {
            dispatch(operations.addArrayElement("listing." + arrayPath));
        },
        handlePropertyDeleteArrayElement: (arrayPath, index) => {
            dispatch(operations.deleteArrayElement("listing." + arrayPath, index));
        },
        handlePropertyUpdateArrayElement: (arrayPath, index, field, value) => {
            dispatch(operations.handleListingInputChange("listing." + arrayPath + "[" + index + "]." + field, value));
        },
        handleListingAddArrayElement: (arrayPath, obj) => {
            dispatch(operations.addArrayElementNEW(arrayPath, obj));
        },
        handleListingDeleteArrayElement: (arrayPath, index) => {
            dispatch(operations.deleteArrayElementNEW(arrayPath, index));
        },
        handleListingUpdateArrayElement: (arrayPath, index, field, value) => {
            dispatch(operations.updateArrayElement(arrayPath, index, field, value));
        },
        handleUpdateContactInfo: (index, field, value) => {
            dispatch(contactOperations.updateContactElement(contactConstants.LISTING_CONTACTS, index, field, value));
        },
        handleAddContactInfo(contactType) {
            dispatch(contactOperations.addContactElement(contactType));
        },
        handleDeleteContactInfo(contactType, index) {
            dispatch(contactOperations.deleteContactElement(contactType, index));
        },
        handleContactInfoCompanyUpdate: (contactType, key, field, value) => {
            dispatch(contactOperations.updateContactElementCompany(contactType, key, field, value));
        },
        brokerSubmitListingRequest: (bypassConfirm) => {
            if (!bypassConfirm) {
                dispatch(utils.operations.toggleConfirm());
            }
            return dispatch(operations.brokerSubmitListingRequest(true));
        },
        clearListingState: () => {
            dispatch(operations.clearListingState());
        },
        handleSearchPerson: (inputValue) => {
            dispatch(searchOperations.searchKMPersonAutocomplete(inputValue));
        },
        handleSearchKMBrokers: (inputValue) => {
            dispatch(searchOperations.searchKMBrokers(inputValue));
        },
        handleSearchCompany: (inputValue, companyType) => {
            dispatch(searchOperations.searchCompany(inputValue, companyType));
        },
        handleSearchOutsideBrokers: (inputValue) => {
            dispatch(searchOperations.searchOutsideBroker(inputValue));
        },
        handleDeleteArrayElement: (path, index) => {
            dispatch(operations.deleteArrayElement("listing." + path, index));
        },
        handleAddArrayElement: (path, obj) => {
            dispatch(operations.addArrayElementNEW("listing." + path, obj));
        },
        handleDeleteArrayElementNEW: (path, index) => {
            dispatch(operations.deleteArrayElementNEW("listing." + path, index));
        },
        handleUpdateArrayElement: (arrayPath, index, field, value) => {
            dispatch(operations.handleListingInputChange("listing." + arrayPath + "[" + index + "]." + field, value));
        },
        clearUtilsErrors: () => {
            dispatch(utils.actions.clearErrors());
        },
        handleSubmitAndUpdateStatus: (newStatus, whatIsSaving, redirectTo) => {
            dispatch(operations.handleSubmitAndUpdateStatus(newStatus, whatIsSaving, redirectTo));
        },
        handleDisplayInactivateDialog: (isOpen) => {
            dispatch(operations.displayInactivateDialog(isOpen));
        },
        handleInactivateListing: (listingKey) => {
            return dispatch(operations.handleInactivateListing(listingKey));
        },
        handleDisplayDeleteDialog: (isOpen) => {
            dispatch(operations.displayDeleteDialog(isOpen));
        },
        handleDeleteListing: (listingKey) => {
            return dispatch(operations.handleDeleteListing(listingKey));
        },
        handleEditProperty: (propertyKey) => {
            dispatch(operations.handleEditProperty());
        },
        toggleRequestModal: (spaceType, transactionType) => {
            dispatch(operations.toggleRequestModal(spaceType, transactionType));
        },
        togglePropertySearchModal: (isOpen) => {
            dispatch(property_operations.togglePropertySearchModal(isOpen));
        },
        handleDuplicateListing: (listingKey) => {
            return dispatch(operations.duplicateListing(listingKey));
        },
        handleListingDuplicateDialog: (isOpen) => {
            dispatch(operations.toggleDuplicateListingDialog(isOpen));
        },
        handleDuplicateSuccessDialog: (isOpen) => {
            dispatch(operations.toggleDuplicateSuccessDialog(isOpen));
        },
        getPropertyDocTypes: (listingId) => {
            dispatch(document_operations.getPropertyDocTypes(listingId));
        },
        copyPropertyPhotoToListing: (listingId) => {
            dispatch(utils.operations.toggleConfirm());
            return dispatch(document_operations.copyPropertyPhotoToListing(listingId));
        },
        checkIfListingIsAlreadyAssigned: (listingKey) => {
            return dispatch(operations.checkIfListingIsAlreadyAssigned(listingKey));
        },
        convertListingType: () => {
            dispatch(operations.convertListingType());
        },
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ListingRequestContainer);
